import type { App } from 'vue';
import type { Router } from 'vue-router';
import * as Sentry from '@sentry/vue';
import axios from 'axios';

import getBackendURL from '@/config/currentSiteMapper';
import ssoAuth from '@/config/sso';
import {
  RAY_ID_HEADER,
  REQUEST_ID_HEADER,
  getClickedText,
  getEmailFromToken,
  normalizePath,
  setSentryLoginEmail,
} from './helpers';

declare type Nullable<T = void> = T | null | undefined;

const toAbsoluteUrl = (url?: string) => {
  if (!url) return undefined;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return undefined;
  }
};

// Login and token refresh failures: reported by reportLoginFailure or expected
// (expired session), so they're dropped from automatic reports.
const SSO_TOKEN_PATH = '/protocol/openid-connect/token';

const isSsoTokenRequestError = (error: unknown) =>
  axios.isAxiosError(error) &&
  !!error.config &&
  axios.getUri(error.config).includes(SSO_TOKEN_PATH);

function getSelfReportedUrls() {
  return [
    toAbsoluteUrl(import.meta.env.VITE_API_URL),
    `https://${getBackendURL()}/`,
  ].filter((url): url is string => !!url);
}

function identifyFromStoredToken() {
  const token = ssoAuth.getToken();
  if (!token) {
    Sentry.setTag('auth_state', 'anonymous');
    return;
  }
  const email = getEmailFromToken(token);
  if (email) setSentryLoginEmail(email);
}

const initializeSentry = (app: App, router: Router) => {
  const sentryDsn: Nullable<string> = import.meta.env.VITE_SENTRY_DSN;
  const environment: string = import.meta.env.VITE_ENVIRONMENT ?? 'dev';

  if (!sentryDsn || environment === 'dev') return;
  const networkDetailAllowUrls: string[] = [
    window.location.origin,
    getBackendURL(),
    import.meta.env.VITE_API_URL,
    import.meta.env.VITE_SSO_URL,
  ].filter(Boolean);
  const selfReportedUrls = getSelfReportedUrls();

  Sentry.init({
    app,
    dsn: sentryDsn,
    release: `${environment}@${__APP_VERSION__}`,
    environment: environment,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        networkDetailAllowUrls: networkDetailAllowUrls,
        networkRequestHeaders: ['Content-Type', REQUEST_ID_HEADER],
        networkResponseHeaders: [
          'Content-Type',
          REQUEST_ID_HEADER,
          RAY_ID_HEADER,
        ],
      }),
      Sentry.replayCanvasIntegration(),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [400, [402, 599]],
      }),
      Sentry.breadcrumbsIntegration({
        dom: {
          serializeAttribute: [
            'aria-label',
            'data-testid',
            'name',
            'placeholder',
            'title',
          ],
        },
      }),
    ],
    beforeBreadcrumb: (breadcrumb, hint) => {
      if (breadcrumb.category === 'ui.click') {
        const text = getClickedText(hint?.event?.target);
        if (text) breadcrumb.message = `${breadcrumb.message} "${text}"`;
      }
      return breadcrumb;
    },
    beforeSend: (event, hint) => {
      if (isSsoTokenRequestError(hint.originalException)) return null;

      const exception = event.exception?.values?.[0];
      if (exception?.mechanism?.type !== 'http.client') return event;

      const url = toAbsoluteUrl(event.request?.url);
      if (
        url &&
        (url.includes(SSO_TOKEN_PATH) ||
          selfReportedUrls.some(prefix => url.startsWith(prefix)))
      ) {
        return null;
      }

      const method = event.request?.method ?? 'GET';
      const path = normalizePath(
        new URL(url ?? '', window.location.origin).pathname,
      );
      const status = String(event.contexts?.response?.status_code ?? '');
      event.fingerprint = ['http-client-error', method, path, status];
      exception.type = 'HttpClientError';
      exception.value = `HTTP ${status} on ${method} ${path}`;
      return event;
    },
    initialScope: {
      tags: {
        site: window.location.hostname,
      },
    },
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      window.location.origin,
      toAbsoluteUrl(import.meta.env.VITE_API_URL),
      `https://${getBackendURL()}/`,
    ].filter((url): url is string => !!url),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  identifyFromStoredToken();
};

export default initializeSentry;
